// Donor-scope recurring-donations API helper — CHG-1513 portal-pivot retarget,
// CHG-2947 (Slice F, DP-R5) server-side paging + filters.
//
// Backend: portal.py canonical at GET /api/v1/my-giving/recurring-donations.
// CHG-2947 made the route param-presence-switched: sending page/per_page
// returns the donor-portal FLAT paged envelope
// ({data, page, per_page, total, total_pages} — same shape as
// /my-giving/donations) with server-side org_id/status filters; sending
// nothing keeps the legacy bare array (v1 api.ts + mobile unchanged).
//
// This helper ALWAYS sends paging params (so the v2 ListPage pages on the
// server instead of fetching the donor's full history every page) and
// normalizes through the CHG-2916 list-contract module, whose CHG-2947
// flat-envelope branch reports the true server total. The pre-CHG-2947
// client-side status/org filtering of the unbounded list is GONE.
//
// Not sent (the route has no such params — dropped no-ops, same convention
// as the CHG-2916 staff helpers): search, sortField, sortDirection.

import { apiClient } from "./client";
import {
  listPageParams,
  normalizeListResponse,
  type NormalizedList,
  type RawListEnvelope,
} from "./list-contract";

export interface PortalRecurringDonationRead {
  id: string;
  organization_id: string;
  org_name: string | null;
  org_logo_url: string | null;
  designation_id: string | null;
  designation_name: string | null;
  donor_name: string | null;
  donor_email: string;
  amount_minor_units: number;
  currency: string;
  frequency: string;
  status: string;
  next_charge_at: string | null;
  created_at: string;
  schedule_expression: Record<string, unknown> | null;
  calendar_key: string | null;
  intent_key: string | null;
  schedule_label: string | null;
}

export interface ListMyRecurringDonationsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  /** Server-side status filter (wire name: `status`) — CHG-2947. */
  recurring_status?: string;
  /** Server-side org filter — CHG-2947. */
  org_id?: string;
}

export async function listMyRecurringDonations(
  params: ListMyRecurringDonationsParams = {},
): Promise<NormalizedList<PortalRecurringDonationRead>> {
  const { page, per_page } = listPageParams(params);
  const r = await apiClient().get<RawListEnvelope<PortalRecurringDonationRead>>(
    "/my-giving/recurring-donations",
    {
      params: {
        page,
        per_page,
        status: params.recurring_status,
        org_id: params.org_id,
      },
    },
  );
  return normalizeListResponse<PortalRecurringDonationRead>(r.data);
}

export const meRecurringApi = {
  listMyRecurringDonations,
};
